/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../core/redux/IAppState";
import { getResource } from "../language/resources/getResource";
import { COLUMN_SPACING } from "../forms/styles";
import { MODAL_SIZE } from "../layout/modal/modalSize";
import ModalView from "../layout/modal/ModalView";
import ModalTitleHeader from "../layout/modal/ModalTitleHeader";
import editIcon from "../assets/icons/edit.svg";
import {
  LiftAccessRuleSetBulkEditor,
  BulkEditSession,
} from "./accessRules/liftAccessRuleSet/LiftAccessRuleSetBulkEditor";
import { GRID_HEIGHT_BUDGET } from "./accessRules/liftAccessRuleSet/bulkEditChangeScan";
import { LiftAccessRuleSet } from "./accessRules/liftAccessRuleSet/models";
import { showSnackbarAction } from "../snackbar/snackbarAction";
import { openPopout, Popout } from "../layout/window/openPopout";
import { PopoutPortal } from "../layout/window/PopoutPortal";
import { PopoutBlocker } from "../layout/window/PopoutBlocker";
import { popoutOpenSize } from "../layout/window/popoutOpenSize";
import { useDockedSize } from "../layout/window/useDockedSize";
import { usePopoutFit } from "../layout/window/usePopoutFit";
import { BLUE_CREEK, COOL_BLUE, DARK_GREY, WHITE } from "../styles/colors";
import * as Buttons from "../forms/elements/buttons";
import type { AccessRuleChangeRequest } from "./accessRules/requests/accessRuleRequestBuilder";

type Props = {
  /** The deduped selection the edit will be applied to. */
  ruleSets: LiftAccessRuleSet[];
  onDismiss: () => void;
  /**
   * Hands the built save requests to the page, which owns the run and reports its
   * progress. This dialog closes as it hands off.
   */
  onApply?: (saveRequests: AccessRuleChangeRequest[]) => void;
};

const headerCss = css`
  position: relative;
  flex: 0 0 auto;
`;

// Pop out / Dock, in the header corner opposite the close button.
const cornerButtonCss = css`
  position: absolute;
  top: 22px;
  left: 20px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 13px;
  border: 1px solid ${BLUE_CREEK};
  border-radius: 4px;
  background: ${WHITE};
  color: ${BLUE_CREEK};
  cursor: pointer;

  &:hover {
    background: ${COOL_BLUE};
  }
`;

// Content padding either side (28 + 28) plus scrollbar room.
const CONTENT_CHROME = 72;

// Also the floor for the review step, so the grid can only widen the dialog.
const COMPOSE_WIDTH = 1000;

const contentWidthPx = (gridWidth: number) =>
  Math.max(COMPOSE_WIDTH, gridWidth + CONTENT_CHROME);

const COMPOSE_HEIGHT = 920;
const REVIEW_CHROME_HEIGHT = 520;

// Sizes are remembered per step: a compact form and a wide grid want very
// different windows.
type PopoutStep = "compose" | "review";

/**
 * The window height a step wants, both to open at and to fit to. Review is budgeted, so
 * a long grid scrolls rather than the window growing.
 *
 * Compose's own height is the modal's, so a pop-out matches the dialog it came from and
 * returning from review comes back to it. 0 until the first pop-out measures it, which
 * leaves the opening size to the docked box.
 *
 * Never derived from the pop-out's own height: an ask measured inside the window it
 * then resizes grows on every pass until it fills the display.
 */
export const fitHeightPx = (
  confirming: boolean,
  gridHeightPx: number,
  dockedHeight: number,
) =>
  confirming
    ? REVIEW_CHROME_HEIGHT + Math.min(gridHeightPx, GRID_HEIGHT_BUDGET)
    : dockedHeight;

// The dialog is bounded by Modal's max-height and is itself a flex column, so
// this is the child that absorbs the leftover height. Nothing scrolls here —
// the scroll belongs to the rule editors alone (see the editor's rulesScrollCss),
// which keeps the title, the mode panel and the buttons in place. Without
// min-height: 0 this grows to fit its content instead, handing the scroll back to
// the modal root (which has overflow-y: auto) and taking the title with it.
const contentCss = css`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding: 0 28px 28px;
  box-sizing: border-box;
  ${COLUMN_SPACING};
`;

// Stands in for Modal's max-height. height, not min-height: the column has to be
// bounded by the window for contentCss to hand the spare height to the grid,
// instead of the window growing past the buttons.
const popoutRootCss = css`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
  background: ${WHITE};
`;

// "Discard changes?" guard. position: fixed centers it in whichever window is
// showing the dialog, docked or popped out.
const discardOverlayCss = css`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgb(0 0 0 / 30%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const discardCardCss = css`
  background: ${WHITE};
  width: 380px;
  max-width: 90vw;
  padding: 24px;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgb(0 0 0 / 25%);
  box-sizing: border-box;
`;

const discardTitleCss = css`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const discardBodyCss = css`
  font-size: 14px;
  color: ${DARK_GREY};
  margin-bottom: 20px;
`;

const discardButtonsCss = css`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

type DiscardConfirmProps = {
  onKeepEditing: () => void;
  onDiscard: () => void;
};

/**
 * Its own component so the focus effect runs in the same root as the card. Inlined in
 * the parent, that effect fires before the pop-out root has committed, so it traps the
 * main window's document instead of the pop-out's.
 */
const DiscardConfirm = ({ onKeepEditing, onDiscard }: DiscardConfirmProps) => {
  const resources = useSelector((state: IAppState) => state.locale);

  // Keeps the effect mount-only while still calling the latest handler.
  const onKeepEditingRef = useRef(onKeepEditing);
  onKeepEditingRef.current = onKeepEditing;

  // Everything reads off the card's own document, which is the pop-out's when detached.
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const card = cardRef.current;
    const doc = card?.ownerDocument ?? document;
    const previouslyFocused = doc.activeElement as HTMLElement | null;
    const focusables = () =>
      Array.from(
        card?.querySelectorAll<HTMLElement>(
          'button, [href], input, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
    (focusables()[0] ?? card)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onKeepEditingRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        card?.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && doc.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && doc.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    doc.addEventListener("keydown", onKeyDown, true);
    return () => {
      doc.removeEventListener("keydown", onKeyDown, true);
      previouslyFocused?.focus?.();
    };
  }, []);

  return (
    <div css={discardOverlayCss}>
      <div
        css={discardCardCss}
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={getResource(
          resources,
          "ACCESS_RULE_BULK_EDIT_DISCARD_TITLE",
        )}
        tabIndex={-1}
      >
        <div css={discardTitleCss}>
          {getResource(resources, "ACCESS_RULE_BULK_EDIT_DISCARD_TITLE")}
        </div>
        <div css={discardBodyCss}>
          {getResource(resources, "ACCESS_RULE_BULK_EDIT_DISCARD_BODY")}
        </div>
        <div css={discardButtonsCss}>
          <Buttons.BigSecondary
            type="button"
            title={getResource(
              resources,
              "ACCESS_RULE_BULK_EDIT_DISCARD_KEEP_TITLE",
            )}
            onClick={onKeepEditing}
          >
            {getResource(resources, "ACCESS_RULE_BULK_EDIT_DISCARD_KEEP")}
          </Buttons.BigSecondary>
          <Buttons.BigNegative
            type="button"
            title={getResource(
              resources,
              "ACCESS_RULE_BULK_EDIT_DISCARD_CONFIRM_TITLE",
            )}
            onClick={onDiscard}
          >
            {getResource(resources, "ACCESS_RULE_BULK_EDIT_DISCARD_CONFIRM")}
          </Buttons.BigNegative>
        </div>
      </div>
    </div>
  );
};

export const AccessMultiEditModal = (props: Props) => {
  const dispatch = useDispatch();
  const resources = useSelector((state: IAppState) => state.locale);
  const title = getResource(resources, "ACCESS_RULE_BULK_EDIT_MODAL_TITLE");

  const [popout, setPopout] = useState<Popout | null>(null);

  // The only parts of the session held as state, because they size the shell. The
  // rest lives in sessionRef, so keystroke edits don't re-render this component.
  const [confirming, setConfirming] = useState(false);
  const [reviewGridWidth, setReviewGridWidth] = useState(0);
  const [reviewGridHeight, setReviewGridHeight] = useState(0);

  // The modal box, measured at pop-out time. Compose has no other natural height.
  const [dockedHeight, setDockedHeight] = useState(0);

  // Must match what popoutOpenSize is given, so compute it once.
  const fitHeight = fitHeightPx(confirming, reviewGridHeight, dockedHeight);

  // Only review is sized to content; compose is a fixed-width form.
  const { rememberedSize, noteOpened } = usePopoutFit<PopoutStep>({
    popout,
    step: confirming ? "review" : "compose",
    targetWidthPx: contentWidthPx(reviewGridWidth),
    // Compose grows to what the editors need; review to what the grid needs.
    targetHeightPx: fitHeight,
    enabled: confirming,
  });

  const { dockedRef, dockedSize } = useDockedSize<HTMLDivElement>();

  const handlePopOut = useCallback(() => {
    const remembered = rememberedSize();

    const docked = dockedSize();
    if (docked) setDockedHeight(docked.height);

    const size = popoutOpenSize({
      remembered,
      docked,
      wantedWidth: confirming ? contentWidthPx(reviewGridWidth) : COMPOSE_WIDTH,
      fitHeight,
      wantedHeight: fitHeight || COMPOSE_HEIGHT,
    });

    const opened = openPopout({
      title,
      width: size.width,
      height: size.height,
    });
    if (!opened) {
      dispatch(
        showSnackbarAction(
          getResource(resources, "ACCESS_RULE_BULK_EDIT_POPUP_BLOCKED"),
        ),
      );
      return;
    }
    // What the opened size was chosen for, so a stale one stays detectable.
    noteOpened(
      remembered?.fitsWidthPx ?? size.width,
      remembered?.fitsHeightPx ?? fitHeight,
    );
    setPopout(opened);
  }, [
    dispatch,
    resources,
    title,
    confirming,
    reviewGridWidth,
    fitHeight,
    rememberedSize,
    noteOpened,
    dockedSize,
  ]);

  // Both docking and the window closing drop the handle; unmounting PopoutPortal
  // closes the window.
  const handleDock = useCallback(() => setPopout(null), []);

  // Held here, not in the editor, so the in-progress session survives the editor
  // remounting as it moves between the docked modal and the window.
  const sessionRef = useRef<BulkEditSession | undefined>(undefined);

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // User-initiated close (✕/Cancel) confirms before dropping the mode selection
  // and anything entered below it.
  const requestClose = useCallback(() => setShowDiscardConfirm(true), []);

  // Hands the requests to the page and closes. The run outlives this dialog, so a
  // pop-out can't stay live behind the progress card and fire a second save.
  const handleApply = useCallback(
    (saveRequests: AccessRuleChangeRequest[]) => {
      if (saveRequests.length === 0) return;
      props.onApply?.(saveRequests);
      props.onDismiss();
    },
    [props],
  );

  const editor = (
    <LiftAccessRuleSetBulkEditor
      ruleSets={props.ruleSets}
      onDismiss={props.onDismiss}
      onApply={handleApply}
      onRequestClose={requestClose}
      initialSession={sessionRef.current}
      onSessionChange={(session) => {
        sessionRef.current = session;
        // Setting the same value is a no-op re-render, which is what keeps
        // keystroke edits from churning the shell.
        setConfirming(session.confirming);
        setReviewGridWidth(session.reviewGridWidth);
        setReviewGridHeight(session.reviewGridHeight);
      }}
    />
  );

  const discardConfirm = showDiscardConfirm && (
    <DiscardConfirm
      onKeepEditing={() => setShowDiscardConfirm(false)}
      onDiscard={() => {
        setShowDiscardConfirm(false);
        props.onDismiss();
      }}
    />
  );

  // Popped out: the dialog renders into the separate window; the page it came from
  // gets the notice and the lock instead of a modal.
  if (popout) {
    return (
      <>
        <PopoutBlocker popout={popout} title={title} onDock={handleDock} />
        <PopoutPortal popout={popout} onClose={handleDock}>
          <div css={popoutRootCss}>
            <div css={headerCss}>
              <button
                type="button"
                css={cornerButtonCss}
                onClick={handleDock}
                title={getResource(resources, "ACCESS_RULE_BULK_EDIT_DOCK")}
              >
                {getResource(resources, "ACCESS_RULE_BULK_EDIT_DOCK")}
              </button>
              <ModalTitleHeader
                resources={resources}
                heading={title}
                icon={editIcon}
                onDismiss={requestClose}
              />
            </div>
            <div css={contentCss}>{editor}</div>
            {discardConfirm}
          </div>
        </PopoutPortal>
      </>
    );
  }

  return (
    <ModalView
      accessibilityModalLabel={title}
      onDismiss={props.onDismiss}
      disableClickoffDismissable
      // The editor holds unsaved work — don't let a stray Escape or backdrop
      // click drop it. Closing is deliberate (✕, Cancel).
      disableKeypressDismissable
      // Capped at the viewport: past that the grid scrolls horizontally rather than
      // compressing its columns.
      size={
        confirming
          ? `min(${contentWidthPx(reviewGridWidth)}px, 96vw)`
          : MODAL_SIZE.LARGE
      }
    >
      {/* First child of the modal box, which is what dockedSize measures. */}
      <div css={headerCss} ref={dockedRef}>
        <button
          type="button"
          css={cornerButtonCss}
          onClick={handlePopOut}
          title={getResource(resources, "ACCESS_RULE_BULK_EDIT_POP_OUT_TITLE")}
        >
          ⇗ {getResource(resources, "ACCESS_RULE_BULK_EDIT_POP_OUT")}
        </button>
        <ModalTitleHeader
          resources={resources}
          heading={title}
          icon={editIcon}
          onDismiss={requestClose}
        />
      </div>
      <div css={contentCss}>{editor}</div>
      {discardConfirm}
    </ModalView>
  );
};
